<template>
  <div class="guide-statistic">
    <div
      v-if="guide.rating || visitorsCount"
      class="guide-statistic-top"
    >
      <div
        v-if="guide.rating"
        class="guide-statistic__rating-wrap"
      >
        <span class="guide-statistic__rating-value">
          <Rating
            :value="5"
            :size="20"
            one-star
          />
          <span class="guide-statistic__rating-text">{{ ratingTextStr }}</span>
        </span>
        <div
          v-if="guide.review_count"
          class="guide-statistic__reviews"
        >
          <a
            class="guide-statistic__rating-link"
            :href="`/guide/${guide.id}/#reviews`"
          >
            {{ ratingLinkStr }}
          </a>
        </div>
      </div>
      <div
        v-if="visitorsCount"
        class="guide-statistic-visitors"
      >
        <TravelerIcon class="guide-statistic-visitors__icon" />
        {{ visitorsStr }}
      </div>
    </div>
    <div class="guide-statistic-duration-and-articles">
      <div class="guide-statistic-duration">
        <CompassIcon class="guide-statistic-duration__icon" />
        {{ durationStr }} на&nbsp;Трипстере
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import type { Guide } from '@/shared/api/experience';
  import { tripsterStandard, ratingWithFloat } from '@experience/money-utils';
  import { choosePlural } from '@experience/shared';
  import { differenceInMonths } from 'date-fns';
  import { Rating } from '@/shared/ui/rating';
  import { TravelerIcon } from '@/shared/ui/icons/travelerIcon';
  import { CompassIcon } from '@/shared/ui/icons/compassIcon';
  import { formatWithComma } from '@/shared/lib/formatWithComma.ts';

  interface Props {
    guide: Guide;
  }

  const props = defineProps<Props>();

  const getPlural = (amount: number, variants: string[]) => {
    const pluralForm = choosePlural(amount, variants);

    return `${tripsterStandard(amount)} ${pluralForm}`;
  };

  const guideDuration = computed(() => {
    const nowDate = new Date();
    const sinceDate = props.guide.is_guide_since ? new Date(props.guide.is_guide_since) : null;
    const diffInMonths = sinceDate ? differenceInMonths(nowDate, sinceDate) : null;
    const [oneMonth, monthsInYear] = [1, 12];

    if (diffInMonths && diffInMonths < oneMonth) {
      return 'Недавно';
    }

    if (diffInMonths && diffInMonths < monthsInYear) {
      return getPlural(diffInMonths, ['месяц', 'месяца', 'месяцев']);
    }

    const years = Math.floor((diffInMonths ?? 0) / monthsInYear);
    const halfYear = diffInMonths && diffInMonths % monthsInYear > 5 ? 0.5 : 0;

    return getPlural(years + halfYear, ['год', 'года', 'лет']).replace('.', ',');
  });

  const visitorsCount = computed(() => props.guide.number_of_persons_paid || 0);
  const reviewCount = computed(() => props.guide.review_count || 0);

  const visitorsStr = computed(() => `${getPlural(visitorsCount.value, ['посетил', 'посетили', 'посетили'])}`);

  const durationStr = computed(() => {
    if (!reviewCount.value || !visitorsCount.value) {
      return 'Недавно';
    }

    return guideDuration;
  });

  const ratingLinkStr = computed(() => `${getPlural(props.guide.review_count || 0, ['отзыв', 'отзыва', 'отзывов'])}`);

  const ratingTextStr = computed(() => `${formatWithComma(ratingWithFloat(props.guide.rating || 0))}`);
</script>

<style lang="scss">
  @use '@experience/design-tokens/typography' as typography;
  @use '@experience/shared/dist/assets/adaptivity' as adaptivity;

  .guide-statistic {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 16px;
    align-items: center;
    margin-bottom: 8px;

    @include typography.base-400-400-regular;

    .guide-statistic-top,
    .guide-statistic-duration-and-articles {
      display: flex;
      flex-wrap: wrap;
      justify-content: center;

      @include adaptivity.mobile {
        flex-direction: column;
        gap: 4px;
        width: 100%;
      }
    }

    .guide-statistic-visitors {
      display: flex;
      flex-wrap: wrap;
      justify-content: center;
      align-items: center;

      .guide-statistic-visitors__icon {
        margin-right: 4px;
        fill: var(--alias-color-icon-default);
      }
    }

    @include adaptivity.mobile {
      gap: 4px;
      margin-bottom: 0;

      @include typography.base-300-300-regular;
    }
  }

  .guide-statistic__rating-wrap {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;
    margin-right: 16px;
  }

  .guide-statistic__rating-value {
    display: flex;
    align-items: center;
    margin-right: 8px;
  }

  .guide-statistic__rating-link,
  .guide-statistic__articles-link {
    color: var(--alias-color-link-default);
    text-decoration: none;

    &:hover,
    &:active {
      color: var(--alias-color-text-accent-magenta);
    }
  }

  .guide-statistic__rating-text {
    margin-left: 4px;
  }

  .guide-statistic-duration {
    display: flex;
    justify-content: center;
    align-items: center;
    margin-right: 12px;

    @include adaptivity.mobile {
      width: 100%;
      margin-right: 0;
    }
  }

  .guide-statistic-duration__icon {
    margin-right: var(--alias-space-4);
    fill: var(--alias-color-icon-default);
  }

  .guide-statistic-articles {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;

    @include adaptivity.mobile {
      width: 100%;
      margin-right: 0;
    }
  }

  .guide-statistic-articles__icon {
    margin-right: var(--alias-space-4);
  }
</style>
